import { selectCurrentHotelId } from 'accessors'
import { PrimaryBigButton } from 'components/ReusableComponents/Button/BigButtons'
import { PrimaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import TextArea from 'features/ManageCampaigns/TextArea'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import subscribersApi from 'services/api/subscribers'
import config from 'services/config'
import LocalUserService from 'services/localUserService'
import { formatPhoneNumber } from 'utils/helpers'

import styles from './GuestBehavior.module.scss'

const baseTags = [
  {
    id: '__hotelName__',
    display: 'hotelName',
    length: 40
  },
  {
    id: '__guestName__',
    display: 'firstName',
    length: 8
  },
  {
    id: '__guestLastName__',
    display: 'lastName',
    length: 8
  }
]

const userService = new LocalUserService()

function addSubscriber({ subsidiaryId, phone, firstName, lastName, isOptIn = true }) {
  return fetch(config.apiUrl + '/subscribers/build/many/fordemo', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'sp-auth': userService.getCurrentUserInfo()?.sessionId
    },
    body: JSON.stringify([
      {
        subsidiaryId,
        phone,
        firstName,
        lastName,
        isOptIn
      }
    ])
  })
}

const MessageSubscribers = () => {
  const [subscriberIds, setSubscriberIds] = useState([])

  const requiredTagIds = []

  const [textContent, setTextContent] = useState('')
  const [usedAllCampaignTags, setUsedAllCampaignTags] = useState(true)
  const [isSendingMessage, setIsSendingMessage] = useState(false)

  const subsidiaryId = useSelector(selectCurrentHotelId)

  const [isTextAreaVisible, setIsTextAreaVisible] = useState(false)
  const [isAddSubscriberDialogVisible, setIsAddSubscriberDialogVisible] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')

  const [includeLink, setIncludeLink] = useState(true)

  const [manuallyAddedSubscribers, setManuallyAddedSubscribers] = useState([])

  function handleAddSubscriber({ firstName, lastName, phone }) {
    addSubscriber({
      subsidiaryId,
      phone: `+1${phone.replace(/\D/g, '')}`,
      firstName,
      lastName
    })
      .then(res => res.json())
      .then(data => {
        const newSubscriber = data[0]

        setManuallyAddedSubscribers([...manuallyAddedSubscribers, newSubscriber])
        setSubscriberIds([...subscriberIds, newSubscriber.id])
        setIsAddSubscriberDialogVisible(false)

        setFirstName('')
        setLastName('')
        setPhone('')
      })

    // const newSubscriber = {
    //   fullName,
    //   phone,
    //   subscriberId
    // }
  }

  function handlePhoneInputChange(e) {
    setPhone(formatPhoneNumber(e.target.value))
  }

  function handlePrimaryCampaignTextChange(text) {
    setTextContent(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  function handleUpdatePrimaryCampaignTags(usedTags) {
    const isAllTagsUsed = requiredTagIds.every(t => usedTags.find(u => u.id === t))

    setUsedAllCampaignTags(isAllTagsUsed)
  }

  const { addToast } = useToasts()

  function sendMessage() {
    setIsSendingMessage(true)

    subscribersApi
      .sendCustomText({
        subscriptionIds: subscriberIds,
        content: textContent,
        includeLink
      })
      .then(() => {
        addToast('Message sent successfully', {
          appearance: 'success',
          autoDismiss: true
        })
        setIsSendingMessage(false)
      })
      .catch(() => {
        addToast('Message failed to send', { appearance: 'error', autoDismiss: true })
        setIsSendingMessage(false)
      })
      .finally(() => {
        setIsSendingMessage(false)
      })
  }

  return (
    <div className={styles.panes}>
      {isAddSubscriberDialogVisible && (
        <ConfirmationDialog
          title="Add subscriber"
          onClose={() => setIsAddSubscriberDialogVisible(false)}
          onConfirm={() =>
            handleAddSubscriber({
              firstName,
              lastName,
              phone,
              subscriberId: phone
            })
          }
        >
          <div className={styles.inputHolder}>
            <TextInput
              label="Phone number"
              type="text"
              className={styles.input}
              onChange={handlePhoneInputChange}
              value={phone}
            />
          </div>
          <div className={styles.inputHolder}>
            {' '}
            <TextInput
              label="First name"
              value={firstName}
              width="100%"
              onChange={e => setFirstName(e.target.value)}
            />
          </div>
          <div className={styles.inputHolder}>
            {' '}
            <TextInput
              label="Last name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
            />
          </div>
        </ConfirmationDialog>
      )}
      <div className={styles.filterPane}>
        <p className={styles.mainTitle}>Subscriber to message</p>

        <div className={styles.columnsHolder}>
          <div style={{ width: '100%' }}>
            <div
              style={{
                paddingBottom: 20,
                borderBottom: '1px solid #dedede',
                marginBottom: 20
              }}
            >
              <PrimaryRegularButton onClick={() => setIsAddSubscriberDialogVisible(true)}>
                Add subscriber
              </PrimaryRegularButton>
            </div>
            <div>
              {manuallyAddedSubscribers.map(({ name, phone, id }) => (
                <div key={id} className={styles.subscriber}>
                  <div className={styles.subscriberName}>{name}</div>
                  <div className={styles.subscriberPhone}>{phone}</div>
                </div>
              ))}
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 14,
                marginTop: 20,
                userSelect: 'none'
              }}
              onClick={() => setIsTextAreaVisible(b => !b)}
            >
              Subscribers ({subscriberIds.length})
            </div>
            <div>
              {isTextAreaVisible && (
                <>
                  <div className={styles.subscriberIds}>{subscriberIds}</div>

                  <textarea
                    style={{
                      padding: 15,
                      fontSize: 15,
                      boxShadow: '0px 1px 2px rgb(0 0 0 / 4%)',
                      border: '1px solid #c0b8b8',
                      borderRadius: 3,
                      width: '100%',
                      height: 100,
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                      boxSizing: 'border-box'
                    }}
                    onChange={e =>
                      e.target.value.trim()
                        ? setSubscriberIds(e.target.value.split(/\r?\n/))
                        : setSubscriberIds([])
                    }
                    placeholder="Enter each subscriber ID on a new line"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          subscriberIds.length <= 0 ? styles.messagePaneDisabled : styles.messagePane
        }
      >
        <div className={styles.messagePaneInner}>
          <p className={styles.mainTitle}>Message guests</p>
          <TextArea
            handleChange={handlePrimaryCampaignTextChange}
            handleUpdateUsedTags={handleUpdatePrimaryCampaignTags}
            value={textContent}
            baseTags={baseTags}
            shouldShowOptOutText={false}
            isErred={!usedAllCampaignTags}
            previewPrefix=""
          />
          <div className={styles.buttonHolder}>
            <div className={styles.includeLinkToggle}>
              <Toggle
                handleChange={e => setIncludeLink(e.target.checked)}
                checked={includeLink}
              />
              Include link
            </div>
            <PrimaryBigButton
              disabled={subscriberIds.length === 0 || isSendingMessage}
              onClick={() => sendMessage()}
            >
              {subscriberIds.length > 0
                ? `Send text to ${subscriberIds.length} guests`
                : 'Send text to guests'}
            </PrimaryBigButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageSubscribers
